<template>
  <div style="margin-top:20px">
    <div style="width: 100%; background-color: #eee; border: 1px dotted grey; margin-bottom: 25px;">
      <span style="font-size: 26px; font-weight: bold">Manage Users</span><br/>
      <span style="font-style: italic; font-size: 18px">Manage user access to the integration portal.</span>
    </div>

    <div v-if="items" class="row" style="margin: 30px 10px 30px 10px; font-weight: bold; font-size: 22px; background-color: white">
      <div class="col" style="border: 1px solid grey;">{{items.length}} of {{items_full.length}}</div>
      <div class="col" style="border: 1px solid grey;"><b-form-select v-model="role_filter" :options="roleOptions" style="font-size: 18px; font-weight: bold; width:200px"/></div>
      <div v-if="job_title_options && job_title_options.length > 2" class="col" style="border: 1px solid grey;"><b-form-select v-model="job_title_filter" :options="job_title_options" style="font-size: 18px; font-weight: bold; width:200px"/></div>
      <div v-if="company_location_options && company_location_options.length > 2" class="col" style="border: 1px solid grey;"><b-form-select v-model="company_location_filter" :options="company_location_options" style="font-size: 18px; font-weight: bold; width:250px"/></div>
      <div style="margin: 5px">
        &nbsp;&nbsp;<b-button variant="success" size="md" :to="{ name: 'user_create' }"><span style="color:white">Add New User</span></b-button>
        &nbsp;&nbsp;<b-button variant="success" size="md" @click="view_import_users = !view_import_users">Import Users</b-button>
        &nbsp;&nbsp;<b-button variant="success" size="md" @click="view_users_help = !view_users_help">More Info</b-button>
      </div>
    </div>

    <b-modal title="Manager Users Info" size="xl" v-model="view_users_help" ok-only>
      <h4>Adding New Users</h4>
      &nbsp;&nbsp;click 'Add New User' to add a new user to this account
      <br/>&nbsp;&nbsp;&nbsp;&nbsp;-> a welcome message will be sent to the user with a link and credentials (not sent for OTHER roles)
      <br/>&nbsp;&nbsp;click 'Import Users' to bulk upload users with a CSV
      <br/>&nbsp;&nbsp;&nbsp;&nbsp;-> a welcome message will be sent to each user with a link and credentials (not sent for OTHER roles)
      <br><hr/>
      <h4>Updating Users</h4>
      &nbsp;&nbsp;click on any user to view the details and update their information including passwords
      <br><hr/>
      <h4>Resetting Passwords</h4>
      &nbsp;&nbsp;from the Manage Profile page, update your password and click 'Save'
      <br/>&nbsp;&nbsp;from the Manager Users page, click 'Reset Password' for any user
      <br/>&nbsp;&nbsp;&nbsp;&nbsp;-> this will generate a new random password and email the user along with a link and new credentials
      <br/>&nbsp;&nbsp;from the Login page, click 'Reset Password', enter the username and click 'Reset & Email New Password'
      <br/>&nbsp;&nbsp;&nbsp;&nbsp;-> this will generate a new random password and email the user along with a link and new credentials
      <br><hr/>
      <h4>Supported User Roles</h4>
      &nbsp;&nbsp;ADMIN - has full access to all features
      <br/>&nbsp;&nbsp;STAFF - has full access to all features except Admin features (Manage Users, Manage Settings, Group Message Sender)
      <br/>&nbsp;&nbsp;DRIVER - only has access to the Fleet Map
      <br/>&nbsp;&nbsp;MANAGER - only has access to the Fleet Map, Reports, VSM & Group Message Sender
      <br/>&nbsp;&nbsp;DISPATCH - only has access to the Fleet Map, VSM & Group Message Sender
      <br/>&nbsp;&nbsp;GARAGE - only has access to the Fleet Map & limited VSM (can update maintenance status, no history)
      <br/>&nbsp;&nbsp;WASH BAY - only has access to the Fleet Map & limited VSM (can update vehicle status, no history)
      <br/>&nbsp;&nbsp;OTHER - has no access to the portal (only used as recipients in the Group Message Sender)
      <br><hr/>

    </b-modal>

    <b-modal title="Import Users" size="xl" v-model="view_import_users" ok-only>
      <span style="padding:50px">
        <b>import file must be a CSV and match the following header & data row format</b>
        <pre>

            username,first_name,last_name,role,tags,job_title,company_location,phone_number
            jsmith@test.com,John,Smith,customer-admin,tag1,Manager,San Diego,15555555555
            bjones@test.com,Bob,Jones,customer,tag2,Sales,LA,15555555556
            bjones@test.com,Bob,Jones,other,tag2,Sales,LA,15555555556

            note - roles must be set to one of the following: admin, staff, driver, dispatch, garage, wash-bay or other

        </pre>
          <b-form-file style="width:400px"
              v-model="file"
              :state="Boolean(file)"
              placeholder="select a file to import users"
              drop-placeholder="Drop file here..."></b-form-file>
          <span>
            <br/><br/><b-btn v-if="file" v-on:click="importUsers()">Import Users</b-btn>
        </span>
      </span>
    </b-modal>

    <div style="margin:20px">
      <b-table :busy="loading" sticky-header="70vh" bordered :items="items" :fields="fields"
               :current-page="currentPage"
               :per-page="perPage">

        <template #table-busy>
          <div class="text-center text-danger my-2">
            <b-spinner class="align-middle"></b-spinner>
          </div>
        </template>

        <template slot="username" slot-scope="row">
          <router-link :to="{ path: 'user_detail?username=' + row.item.username}">{{row.item.username}}</router-link>
        </template>

        <template slot="role" slot-scope="row">
          <span v-if="row.item.role == 'customer-admin'">Admin</span>
          <span v-else-if="row.item.role == 'customer'">Staff</span>
          <span v-else>{{ row.item.role | initCaps}}</span>
        </template>

        <template slot="accounts" slot-scope="row">
          <span v-for="tenant in row.item.accounts.split(',')">
            {{tenant}}<br/>
          </span>
        </template>

        <template slot="last_login_time" slot-scope="row">
          {{row.item.last_login_time | formatDateTime}}
        </template>

        <template slot="actions" slot-scope="row">
            <b-button size="sm" @click="reset_user_password(row.item.username)">Reset Password</b-button>&nbsp;
            <b-button size="sm" @click="delete_user(row.item.username)">Delete</b-button>&nbsp;
        </template>
      </b-table>
    <b-row>
      <b-col>
        <b-pagination
            v-model="currentPage"
            :total-rows="items.length"
            :per-page="perPage"
            align="center"
        >
        </b-pagination>
      </b-col>
    </b-row>
    </div>

  </div>
</template>

<script>

    import {get_users, reset_user_password, delete_user} from '../networking/apis'
    import {mapState} from "vuex";
    import axios from "axios";
    import moment from "moment/moment";

    export default {
      name: 'user_list',
      components: {
      },

      data() {
        return {
          view_import_users: false,
          view_users_help: false,
          toggle: {},
          file: null,
          msg: 'users',
          items: [],
          items_full: [],
          selected_status: null,
          summary: [],
          job_title_options: [],
          company_location_options: [],
          role_options: [],
          role_filter: '',
          job_title_filter: '',
          company_location_filter: '',
          tag_filter: '',
          loading: true,
          totalRows: 1,
          currentPage: 1,
          perPage: 400,
          pages: [],
          fields: [
            { key: 'username', label: 'Username', sortable: true},
            { key: 'first_name', label: 'First Name', sortable: true},
            { key: 'last_name', label: 'Last Name', sortable: true},
            { key: 'phone_number', label: 'Phone #', sortable: true},
            { key: 'role', label: 'Role', sortable: true},
            { key: 'job_title', label: 'Job Title', sortable: true},
            { key: 'company_location', label: 'Company/Location', sortable: true},
            { key: 'accounts', label: 'Accounts', sortable: true, thStyle: { width: "250px" }},
            { key: 'last_login_time', label: 'Last Login', sortable: true},
            { key: 'actions', label: 'Actions'},
          ],
          roleOptions: [
            { value: '', text: 'All Roles' },
            { value: 'customer-admin', text: 'Admin' },
            { value: 'customer', text: 'Staff' },
            { value: 'driver', text: 'Driver' },
            { value: 'customer-care', text: 'Customer Care' },
            { value: 'manager', text: 'Manager' },
            { value: 'dispatch', text: 'Dispatch' },
            { value: 'garage', text: 'Garage' },
            { value: 'wash-bay', text: 'Wash Bay' },
            { value: 'other', text: 'Other' },
          ],
        }
      },

      async created() {
        await this.load_data()
      },
      methods: {
        toggle_div(item,field) {
          item[field] = !item[field];
          this.$forceUpdate();
        },
        async load_data() {
          this.loading = true
          try {
            const {value: { data = [] } } = await get_users(this.user.tenant)
            this.summary = data.summary
            moment.tz.setDefault(this.summary.timezone)
            this.items = data.results
            this.items_full = data.results
            this.job_title_options = data.summary.job_titles
            this.company_location_options = data.summary.company_locations

          } catch(e) {
            this.$emit('showAlert', 'error retrieving data, try logging in again or contact support if needed', 'danger', 5000)
          }

          this.loading = false
        },
        async reset_user_password(username) {
          try{
              this.$emit('showAlert', "resetting password...", 'info')

              const { data } = await axios.put(
                  process.env.VUE_APP_API_URL + "bookings/reset-password",
                  {username: username},
                  { headers: {'Access-Control-Allow-Origin': '*'}}
              )
              this.$emit('showAlert', "an email with a new password has been sent to " + username, 'success', 5000)

          } catch(e){
              e.response && e.response.data && e.response.data.message
              ? this.$emit('showAlert', e.response.data.message, 'danger')
              : this.$emit('showAlert', "reset password failed", 'danger')
          }
        },
        async delete_user(username) {
          this.$emit('showAlert', "deleting User..." + username, 'info')
          const result = await delete_user(this.user.tenant,username)
          this.$emit('showAlert', "user " + username + " has been deleted", 'success', 5000)
          await this.load_data()
        },
        async importUsers() {
          this.$emit('showAlert', "importing users...", 'info')

          let formData = new FormData();
          formData.append('file', this.file);
          axios.post( process.env.VUE_APP_API_URL + 'bookings/users/import/' + this.user.tenant,
              formData,
              {
                headers: {
                  Authorization: `Bearer ${localStorage.getItem('jwt')}`,
                  'Content-Type': 'multipart/form-data'
                }
              })
              .then(({ data, message }) => {
                this.$emit('showAlert', "users have been imported", 'success', 5000)
                this.load_data()
                this.file = null
              })
              .catch(e => {
                this.$emit('showAlert', "error importing users", 'danger', 5000)
                this.file = null
              });

        },
        filter_results() {
          this.items = this.items_full
          if (this.role_filter != '')
            this.items = this.items.filter(item => (item.role && item.role == this.role_filter))
          if (this.job_title_filter != '')
            this.items = this.items.filter(item => (item.job_title && item.job_title == this.job_title_filter))
          if (this.company_location_filter != '')
            this.items = this.items.filter(item => (item.company_location && item.company_location == this.company_location_filter))
        }
      },
      computed:{
        ...mapState({
          user: state => state.profile.user
        }),
      },
      watch: {
        user: {
          deep: true,
          handler() {
            this.load_data()
          }
        },
        role_filter() {
          this.filter_results()
        },
        tag_filter() {
          this.filter_results()
        },
        company_location_filter() {
          this.filter_results()
        },
        job_title_filter() {
          this.filter_results()
        },
      }
    }
</script>

<style>

  .col { border-radius: 5px; padding:3px}

  div.row:nth-child(even) {
    background-color: white !important;
  }
</style>

